<script setup lang="ts">
import { useScrollLock } from "@vueuse/core";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import PlusWhite from "#/assets/iconsDynamic/PlusWhite.vue";
import { useCardStore } from "#/store/card";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { buildCartProductFromCardState } from "#/utils/buildCartProduct";
import CardMain from "../cardMain/CardMain.vue";
import PopupComboAdd from "../popupComboAdd/PopupComboAdd.vue";

const props = defineProps<{
	visible: boolean;
	qr_menu?: boolean;
}>();

const scrollLock = useScrollLock(document.body);
const { t } = useTranslation();

const cardStore = useCardStore();
const decor = useDecorationStore().screen("main");
const mainStore = useMainStore();
const { dataCombo, stateCard, isCombo } = storeToRefs(cardStore);

const comboList = computed(() => (Array.isArray(dataCombo.value) ? dataCombo.value : []));

const isVisible = computed(() => props.visible);
const isComboAddVisible = ref(false);
const selectedCombo = ref<any | null>(null);
const popupComboInfo = ref<HTMLElement | null>(null);
const isOverflowing = ref(false);

const emit = defineEmits<{
	(e: "update:visible", value: boolean): void;
	(e: "close-product"): void;
}>();

const checkOverflow = () => {
	if (!popupComboInfo.value) return;
	const el = popupComboInfo.value;
	isOverflowing.value = el.scrollWidth > el.clientWidth + 1;
};

const showPopup = async () => {
	scrollLock.value = true;
	if (stateCard.value) stateCard.value.isSectionModifier = false;
	await nextTick();

	checkOverflow();
};

const resetComboScroll = () => {
	if (popupComboInfo.value) {
		popupComboInfo.value.scrollLeft = 0;
	}
};

const hidePopup = () => {
	scrollLock.value = false;
	isCombo.value = false;
	emit("update:visible", false);
};

watch(
	() => props.visible,
	async (newValue) => {
		if (newValue) {
			await showPopup();
		} else {
			hidePopup();
		}
	},
	{ immediate: false },
);

watch(
	() => isCombo.value,
	(newValue) => {
		if (!newValue && props.visible) {
			hidePopup();
		}
	},
);

watch(
	comboList,
	() => {
		nextTick(checkOverflow);
	},
	{ flush: "post" },
);

watch(dataCombo, (value) => {
	if (props.visible && Array.isArray(value) && value.length === 0) {
		handleAddMainDishToCart();
	}
});

const handleResize = () => {
	checkOverflow();
};

onMounted(() => {
	window.addEventListener("resize", checkOverflow);
	if (props.visible) showPopup();
});

onUnmounted(() => {
	window.removeEventListener("resize", checkOverflow);
	scrollLock.value = false;
	resetComboScroll();
});

const goClose = () => hidePopup();

const handleAddMainDishToCart = () => {
	if (!stateCard.value.openCard) {
		console.warn("openCard is not set");
		return;
	}

	mainStore.addToCart(buildCartProductFromCardState(cardStore.stateCard as never));
	isCombo.value = false;
	cardStore.isAddToCard = true;
	hidePopup();
	emit("close-product");
};

const handleAddComboToCart = (product: any) => {
	selectedCombo.value = product;
	isComboAddVisible.value = true;
};

const handleClose = () => {
	isComboAddVisible.value = false;
	isCombo.value = false;
	resetComboScroll();
	hidePopup();
};

watch(isComboAddVisible, (newVal) => {
	if (!newVal) resetComboScroll();
});
</script>

<template>
	<div class="background_black" ref="backgroundBlack" v-show="isVisible" @click="hidePopup"></div>
	<div ref="popupOverlay" class="popup_overlay_combo" v-show="isVisible">
		<div class="popup_combo_wrapper">
			<div class="popup_combo_header">
				<p class="text_combo">{{ t(`popup-combo.title`) }}</p>
				<div class="header_close_combos" @click="goClose">
					<PlusWhite class="close_icon" />
				</div>
			</div>
			<div class="popup_combo_wrapper_container">
				<div class="popup_combo_info" ref="popupComboInfo" :class="{ 'popup_combo_info_center': !isOverflowing }">
					<CardMain
						v-if="stateCard.openCard"
						:data="stateCard.openCard"
						:key="`main-${stateCard.openCard.id}`"
						:parent="stateCard.openCard.id"
						:is_group="false"
						:hideAddButton="true"
						:hideLike="true"
						:activeTags="[]"
						:isLoading="false"
						:disableComboSearch="true"
						:priceOverride="cardStore.totalPrice_libCard"
						:onAddToCart="handleAddMainDishToCart"
						@click="handleAddMainDishToCart"
						class="card"
						decoration="main"
						v-bind="decor.bind('card')"
					/>
					<CardMain
						v-for="(card) in comboList"
						:data="card"
						:key="card.id"
						:parent="card.id"
						:is_group="false"
						:hideAddButton="true"
						:hideLike="true"
						:activeTags="[]"
						:isLoading="false"
						:disableComboSearch="true"
						:onAddToCart="handleAddComboToCart"
						@click="handleAddComboToCart(card)"
						class="card"
						decoration="main"
						v-bind="decor.bind('card')"
					/>
				</div>
			</div>
		</div>
	</div>
	<PopupComboAdd
		v-model:visible="isComboAddVisible"
		:comboData="selectedCombo || undefined"
		:qr_menu="props.qr_menu"
		@close-all="handleClose"
		@close-product="emit('close-product')"
	/>
</template>

<style lang="scss">
  @use './popupCombo.scss'
</style>
